import React from "react";
import {Link} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {ChevronDown, LogOut, Send, Settings, KeyRound} from "@/components/icons";
import {requestAccountReset, fetchAccountResetAvailability, type ResetChannel} from "@/lib/api";
import {useResetPassword} from "@/components/ResetPasswordAction";
import {ChangePasswordDialog} from "@/components/ChangePasswordDialog";
import {toast} from "sonner";
import {useT} from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";
import {UserAvatar} from "@/components/icons";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from "@/components/ui/dropdown-menu";


export default function UserMenu({user}: { user: { id: number, username: string; role: string } | null }) {
    const t = useT();
    // Availability + masked destinations come from the backend (authoritative);
    // the reset itself is always bound server-side to the current session user.
    const availabilityQ = useQuery({
        queryKey: ["reset-availability", "account"],
        queryFn: fetchAccountResetAvailability,
        enabled: Boolean(user),
    });
    const avail = availabilityQ.data;

    async function initiate(channel: ResetChannel) {
        try {
            await requestAccountReset(channel);
            toast.success(t(channel === "sms" ? "operator.user_menu.reset_sent_sms" : "operator.user_menu.reset_sent_email"));
        } catch (e: any) {
            // Cooldown, rate limit, delivery and configuration codes each have their message.
            toast.error(errorMessage(t, e, { family: "account_send_reset", fallback: "operator.user_menu.reset_failed" }));
        }
    }

    const reset = useResetPassword({
        emailAvailable: avail?.email_reset_available ?? false,
        smsAvailable: avail?.sms_reset_available ?? false,
        maskedEmail: avail?.masked_email,
        maskedPhone: avail?.masked_phone,
        loading: availabilityQ.isLoading,
        error: availabilityQ.isError,
        initiate,
    });

    // Self-service "Change password" — set a new password directly while logged
    // in (no recovery link/code). Rendered outside the dropdown so it survives
    // the menu closing, same pattern as the reset dialog.
    const [changeOpen, setChangeOpen] = React.useState(false);

    return (
        <>
            {!user && null}
            {user &&
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            className="
                                inline-flex items-center gap-3
                                h-auto min-h-0 px-2 py-1.5
                                rounded-2xl
                                hover:bg-muted
                                transition-colors outline-none
                            "
                        >
                            <UserAvatar className="w-10 rounded-full" />

                            {/* Visually hidden on phones, where it still names the button. */}
                            <div className="flex flex-col items-start leading-tight max-sm:sr-only">
                                <span className="text-sm font-medium text-foreground">
                                  {user.username}
                                </span>
                                <span className="text-xs text-foreground/60">
                                  {user.role === "admin" || user.role === "user"
                                      ? t("operator.user_menu.system_role", {role: user.role})
                                      : user.role}
                                </span>
                            </div>

                            <ChevronDown className="h-4 w-4 text-foreground/50"/>
                        </button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end" className="w-64">
                        {/* Always shown: the account page hosts the personal language besides the optional SMS/Google sections. */}
                        <DropdownMenuItem asChild>
                            <Link to="/account">
                                <Settings className="h-4 w-4"/>
                                {t("operator.user_menu.account_settings")}
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => setChangeOpen(true)}>
                            <KeyRound className="h-4 w-4"/>
                            {t("operator.user_menu.change_password")}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            disabled={reset.disabled}
                            title={reset.title}
                            onSelect={() => reset.onTrigger()}
                        >
                            <Send className="h-4 w-4"/>
                            {reset.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                            <a href="/logout">
                                <LogOut className="h-4 w-4"/>
                                {t("operator.user_menu.sign_out")}
                            </a>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>}

            {/* Rendered OUTSIDE the dropdown so it survives the menu closing. */}
            {reset.dialog}
            {user &&
                <ChangePasswordDialog
                    open={changeOpen}
                    onClose={() => setChangeOpen(false)}
                    userId={user.id}
                    role={user.role}
                />}
        </>
    )
}
